import { useMemo, useState } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import {
    DataTable,
    MoreFiltersMenu,
    expandAction,
    visibleColumns,
    type DataTableAction
} from '@odo/shared-ui';
import { useMyPermissions } from '../../../hooks/useMyPermissions';
import { PURCHASE_ORDERS_SEARCH_MAX_LENGTH } from '../../../services/purchaseOrders';
import { ChargeDrawer } from '../ChargeDrawer';
import { CreatePurchaseOrderModal } from './create/CreatePurchaseOrderModal';
import { ExportPurchaseOrdersModal } from './ExportPurchaseOrdersModal';
import { PurchaseOrderDrawer } from './PurchaseOrderDrawer';
import { PurchaseOrderStatusCards } from './PurchaseOrderStatusCards';
import { buildPurchaseOrderColumns } from './purchaseOrderColumns';
import {
    usePurchaseOrdersListState,
    type PurchaseOrderFilterName
} from './usePurchaseOrdersListState';

// Table and drawer hosting, shared by the org-wide page and a practice-scoped
// AP tab (`practiceId` scopes the query). List state lives in the hook.

export interface PurchaseOrdersContentProps {
    /** Scopes the query to one practice and hides the practice column. */
    practiceId?: string;
    /** Scopes the list to the purchase orders raised by one charge. */
    chargeId?: string;
    // Drawer state is pluggable: the org page drives it from the route so the
    // PO is deep-linkable, an embedded instance uses local state.
    openPurchaseOrderId: string | null;
    onOpenPurchaseOrder: (id: string) => void;
    onClosePurchaseOrder: () => void;
    /** Per-filter sessionStorage keys, as `useDataTable` takes them. */
    filterStorageKeys: Readonly<Record<PurchaseOrderFilterName, string>>;
    /** Fixed-height panel with its own scroll region, for embedding. */
    fillHeight?: boolean;
    onTotalCountChange?: (count: number) => void;
}

export function PurchaseOrdersContent({
    practiceId,
    chargeId,
    openPurchaseOrderId,
    onOpenPurchaseOrder,
    onClosePurchaseOrder,
    filterStorageKeys,
    fillHeight = false,
    onTotalCountChange
}: PurchaseOrdersContentProps) {
    const list = usePurchaseOrdersListState({
        practiceId,
        chargeId,
        filterStorageKeys,
        fillHeight,
        onTotalCountChange
    });
    const { hasPermission } = useMyPermissions();
    const canEdit = hasPermission('billing.update');

    const [openChargeId, setOpenChargeId] = useState<string | null>(null);
    const [showCreatePo, setShowCreatePo] = useState(false);
    const [showExport, setShowExport] = useState(false);

    // The charge drawer overlays the PO drawer; while a PO is open the charge
    // drawer gets a back chevron returning to it.
    const closeCharge = () => {
        setOpenChargeId(null);
        if (openPurchaseOrderId) onClosePurchaseOrder();
    };

    const { showPracticeColumn } = list;
    const columns = useMemo(
        () => buildPurchaseOrderColumns({ showPracticeColumn }),
        [showPracticeColumn]
    );

    return (
        <div
            className={
                fillHeight
                    ? 'flex flex-col h-full min-h-0'
                    : 'flex flex-col h-full overflow-hidden bg-gray-100'
            }
        >
            <div className="shrink-0">
                <PurchaseOrderStatusCards
                    stageSummary={list.stageSummary}
                    current={list.stageFilters}
                    onChange={list.setStageFilters}
                    className={fillHeight ? '' : undefined}
                />
            </div>

            <DataTable
                label="Purchase orders"
                columns={columns}
                rows={list.rows}
                getRowKey={(po) => po.id}
                rowHeightClassName="h-16"
                rowAction={{ onActivate: (po) => onOpenPurchaseOrder(po.id), mode: 'click' }}
                sort={list.sort}
                mode={list.mode}
                onModeChange={list.setMode}
                pagination={{
                    page: list.page,
                    totalPages: list.totalPages,
                    total: list.total,
                    onPage: list.setPage
                }}
                onLoadMore={list.loadMore.fetchNextPage}
                hasMore={list.loadMore.hasMore}
                isLoadingMore={list.loadMore.isLoadingMore}
                isLoading={list.isLoading}
                loadingRows={
                    <tr>
                        <td
                            colSpan={visibleColumns(columns).length}
                            className="px-3 py-8 text-center"
                        >
                            <div className="flex items-center justify-center gap-2 text-gray-400">
                                <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                                <span className="text-sm">Loading purchase orders…</span>
                            </div>
                        </td>
                    </tr>
                }
                emptyState="No purchase orders found."
                refs={list.refs}
                className={fillHeight ? 'overflow-hidden' : 'overflow-hidden px-8 pb-8 pt-6'}
                search={{
                    value: list.searchInput,
                    onChange: list.setSearchInput,
                    placeholder: 'Search PO #, vendor, vendor invoice #, or patient name',
                    maxLength: PURCHASE_ORDERS_SEARCH_MAX_LENGTH
                }}
                filters={list.filters}
                toolbar={
                    <>
                        {list.isFetching && !list.isLoading && (
                            <span className="text-xs text-gray-400">Refreshing…</span>
                        )}
                        <MoreFiltersMenu
                            categories={list.moreFilters}
                            allCategories={list.moreFilters}
                            hiddenActiveCount={list.moreFiltersActiveCount}
                        />
                    </>
                }
                actions={
                    [
                        ...(fillHeight
                            ? [expandAction(list.expansion, { label: 'Purchase orders' })]
                            : []),
                        {
                            key: 'export',
                            label: 'Export',
                            icon: <ArrowDownTrayIcon className="h-4 w-4" />,
                            onClick: () => setShowExport(true)
                        },
                        ...(canEdit
                            ? [
                                  {
                                      key: 'create-po',
                                      label: '+ Create PO',
                                      variant: 'primary' as const,
                                      onClick: () => setShowCreatePo(true)
                                  }
                              ]
                            : [])
                    ] satisfies DataTableAction[]
                }
                expansion={fillHeight ? list.expansion : undefined}
            />

            {openPurchaseOrderId && !openChargeId && (
                <PurchaseOrderDrawer
                    purchaseOrderId={openPurchaseOrderId}
                    onClose={onClosePurchaseOrder}
                    onOpenCharge={setOpenChargeId}
                />
            )}

            {openChargeId && (
                <ChargeDrawer
                    chargeId={openChargeId}
                    onClose={closeCharge}
                    onBack={openPurchaseOrderId ? () => setOpenChargeId(null) : undefined}
                />
            )}

            {showCreatePo && (
                <CreatePurchaseOrderModal
                    initialPracticeId={practiceId}
                    onClose={() => setShowCreatePo(false)}
                    onCreated={(created) => onOpenPurchaseOrder(created.id)}
                />
            )}

            {showExport && (
                <ExportPurchaseOrdersModal
                    practiceId={practiceId}
                    chargeId={chargeId}
                    onClose={() => setShowExport(false)}
                />
            )}
        </div>
    );
}
